import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Instagram, CalendarClock, PartyPopper, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BUSINESS } from "@/lib/business";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", order_type: "pickup", address: "", notes: "", pickup_date: "", pickup_time: "" };

const minPickupDate = () => new Date(Date.now() + 2 * 86400000).toISOString().split("T")[0];

export const OrderForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { items: cart, setQty, removeItem, total, clear } = useCart();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Your cart is empty — add dishes from the menu first.");
      return;
    }
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name and phone.");
      return;
    }
    if (!form.pickup_date) {
      toast.error("Please choose a pickup date.");
      return;
    }
    const itemsText = cart
      .map((i) => `${i.qty}x ${i.name} ($${i.price.toFixed(2)}) = $${(i.price * i.qty).toFixed(2)}`)
      .join("\n");
    const pickupWhen = form.pickup_time ? `${form.pickup_date} at ${form.pickup_time}` : form.pickup_date;
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders`, { ...form, items: itemsText, total, pickup_time: pickupWhen });
      setDone(true);
      toast.success("Order request sent! We'll reach out shortly.");
      setForm(EMPTY);
      clear();
    } catch (err) {
      toast.error("Something went wrong. Please DM us on Instagram instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "border-line focus:border-brand focus:ring-brand rounded-lg bg-white h-12";

  return (
    <section id="order" data-testid="order-section" className="py-20 sm:py-28 bg-cream">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm tracking-[0.25em] uppercase text-brand font-bold">Order Request</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-ink">Place your order</h2>
          <p className="mt-4 text-inkmuted">Send us your order and we'll confirm the details — or message us on <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" className="text-brand font-semibold hover:underline">Instagram</a>.</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 bg-white rounded-3xl border border-line shadow-sm p-6 sm:p-10"
        >
          {done ? (
            <div className="text-center py-10" data-testid="order-success">
              <CheckCircle2 className="mx-auto text-olive" size={56} />
              <h3 className="mt-4 font-serif text-2xl font-semibold text-ink">Thank you!</h3>
              <p className="mt-2 text-inkmuted">Your order request was sent. We'll reach out to confirm timing and payment.</p>
              <button onClick={() => setDone(false)} data-testid="order-another" className="mt-6 rounded-full bg-brand text-white px-8 py-3 font-bold hover:bg-brand-hover transition-colors">
                Place another order
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="order-form">
              <div data-testid="order-advance-notice" className="space-y-2">
                <div className="flex items-center gap-3 bg-gold/15 border border-gold/40 rounded-xl px-4 py-3">
                  <CalendarClock className="text-brand shrink-0" size={20} />
                  <p className="text-sm font-semibold text-ink">Please order at least <span className="text-brand">2 days in advance</span>.</p>
                </div>
                <div className="flex items-center gap-3 bg-brand/10 border border-brand/30 rounded-xl px-4 py-3">
                  <PartyPopper className="text-brand shrink-0" size={20} />
                  <p className="text-sm font-semibold text-ink">Catering (parties &amp; weddings)? Please order at least <span className="text-brand">1 week ahead</span>.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-ink font-semibold">Full name</Label>
                  <Input id="name" data-testid="order-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-ink font-semibold">Phone</Label>
                  <Input id="phone" data-testid="order-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(434) 000-0000" className={inputCls} />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-ink font-semibold">Order type</Label>
                <div data-testid="order-type" className="rounded-xl border border-brand bg-brand/5 text-brand px-4 py-3 flex items-center gap-2 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-brand" /> Pickup
                </div>
                <p className="text-xs text-inkmuted">Delivery is temporarily unavailable — pickup only for now.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="pickup_date" className="text-ink font-semibold">Pickup date</Label>
                  <Input id="pickup_date" type="date" data-testid="order-pickup-date" min={minPickupDate()} value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className={inputCls} />
                  <p className="text-xs text-inkmuted">Earliest is 2 days from today.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pickup_time" className="text-ink font-semibold">Preferred pickup time</Label>
                  <Input id="pickup_time" type="time" data-testid="order-pickup-time" value={form.pickup_time} onChange={(e) => set("pickup_time", e.target.value)} className={inputCls} />
                  <p className="text-xs text-inkmuted">We'll confirm the exact time with you.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-ink font-semibold">Your order</Label>
                {cart.length === 0 ? (
                  <div data-testid="order-cart-empty" className="rounded-xl border border-dashed border-line bg-surface2 px-5 py-6 text-center">
                    <ShoppingBag className="mx-auto text-line" size={32} />
                    <p className="mt-2 text-sm text-inkmuted">Your cart is empty.</p>
                    <a href="#menu" className="mt-3 inline-block text-brand font-semibold hover:underline">Browse the menu ↑</a>
                  </div>
                ) : (
                  <div data-testid="order-cart-list" className="rounded-xl border border-line bg-surface2 divide-y divide-line overflow-hidden">
                    {cart.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 px-4 py-3">
                        <img src={it.image} alt={it.name} className="w-12 h-12 rounded-lg object-cover border border-line" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">{it.name}</p>
                          <p className="text-xs text-brand font-bold">${it.price.toFixed(2)}</p>
                        </div>
                        <div className="inline-flex items-center gap-2 bg-white border border-line rounded-full px-1">
                          <button type="button" onClick={() => setQty(it.id, it.qty - 1)} className="w-7 h-7 flex items-center justify-center text-ink hover:text-brand" aria-label="Decrease"><Minus size={13} /></button>
                          <span className="w-5 text-center text-sm font-bold">{it.qty}</span>
                          <button type="button" onClick={() => setQty(it.id, it.qty + 1)} className="w-7 h-7 flex items-center justify-center text-ink hover:text-brand" aria-label="Increase"><Plus size={13} /></button>
                        </div>
                        <span className="w-16 text-right text-sm font-bold text-ink">${(it.price * it.qty).toFixed(2)}</span>
                        <button type="button" onClick={() => removeItem(it.id)} className="text-inkmuted hover:text-brand" aria-label="Remove"><Trash2 size={15} /></button>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-4 py-3 bg-white">
                      <span className="font-semibold text-ink">Subtotal</span>
                      <span data-testid="order-total" className="font-serif text-xl font-bold text-brand">${total.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-ink font-semibold">Notes (optional)</Label>
                <Textarea id="notes" data-testid="order-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Allergies, spice level, preferred pickup time…" rows={2} className="border-line focus:border-brand focus:ring-brand rounded-lg bg-white" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button type="submit" disabled={submitting || cart.length === 0} data-testid="order-submit" className="flex-1 rounded-full bg-brand text-white py-4 font-bold hover:bg-brand-hover transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {submitting && <Loader2 className="animate-spin" size={18} />} Send order request
                </button>
                <a href={BUSINESS.instagramUrl} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full text-white py-4 font-bold shadow-md bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90 transition-opacity">
                  <Instagram size={18} /> Order on Instagram
                </a>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
